import { normalizePostalCode, normalizeTel, postalCodeMessage, telMessage } from "./billing-formats.js";
import { isEmailLike, type FieldRules, type FormRules, type LengthRule } from "./validation.js";

/**
 * 送る前の検証の規則。<b>どれも Implem.Portal.Backend の ViewModel（<c>src/Implem.Portal.Api/Models/</c>）の
 * DataAnnotations の写し</b>で、文言は ErrorMessage と Display の Name をそのまま写す。
 * <b>ViewModel の属性を変えたら、ここも同じに直す</b>——直さないと、画面と API で違う文が出る。
 *
 * 対象は属性（Required・StringLength・EmailAddress・Compare・Range）で決まる欄と、ご契約者の連絡先（Validate の中の必須と形式）。
 * 人数・ストレージなど、プランと突き合わせる IValidatableObject の判定は写さない（API に任せる）。
 */

const requiredMessage = "{0}は必ず入力してください。";
const emailMessage = "メールアドレスの形式が正しくありません。";
const passwordLength: LengthRule = { max: 100, min: 10, message: "{0}は {2} 文字以上で入力してください。" };
const mismatchMessage = "パスワードと確認のパスワードが一致しません。";

/** RegisterViewModel・ForgotPasswordViewModel・ResetPasswordViewModel の Email。 */
const email: FieldRules = { display: "メールアドレス", required: requiredMessage, email: emailMessage };

/** LoginViewModel。 */
export const loginRules: FormRules = {
    Email: {
        display: "メールアドレス",
        required: "メールアドレスを入力してください。",
        email: "メールアドレスの形式が正しくありません。",
    },
    Password: { display: "パスワード", required: "パスワードを入力してください。" },
};

/** ResetPasswordViewModel（RegisterViewModel もこの欄を同じ属性で持つ）。 */
export const newPasswordRules: FormRules = {
    Email: email,
    Password: { display: "パスワード", required: requiredMessage, length: passwordLength },
    ConfirmPassword: { display: "パスワードの確認入力", compare: { other: "Password", message: mismatchMessage } },
};

/** RegisterViewModel（ResetPasswordViewModel の欄に、利用規約への同意を足したもの）。 */
export const registerRules: FormRules = {
    ...newPasswordRules,
    Agree: { display: "利用規約に同意する", mustBeTrue: "利用規約に同意が必要です。" },
};

/** ForgotPasswordViewModel。 */
export const forgotPasswordRules: FormRules = { Email: email };

/** ChangePasswordViewModel。 */
export const changePasswordRules: FormRules = {
    OldPassword: { display: "現在のパスワード", required: requiredMessage },
    NewPassword: { display: "新しいパスワード", required: requiredMessage, length: passwordLength },
    ConfirmPassword: {
        display: "新しいパスワードの確認入力",
        compare: { other: "NewPassword", message: mismatchMessage },
    },
};

/** VerifyTotpViewModel（ログインの確認コードと、認証アプリの登録の両方が受ける）。 */
export const totpRules: FormRules = {
    Code: { display: "確認コード", required: "確認コードを入力してください。" },
};

/** PlanSelectionInput の PlanName（申し込みとプラン変更で共通）。 */
export const planRules: FormRules = {
    PlanName: { display: "料金プラン", required: requiredMessage },
};

/** ご契約者の連絡先の「有料プランでは◯◯の入力が必要です。」（Validate の中の必須）。 */
const paidRequired = "有料プランでは{0}の入力が必要です。";

const signupLength = (max: number): LengthRule => ({ max, message: "{0}は{1}文字以内で入力してください。" });

/** TenantRegisterViewModel（申し込みにしか無い欄）。PlanSelectionInput の分は <c>planRules</c>。 */
export const signupRules: FormRules = {
    ...planRules,
    Company: { display: "企業名/組織名", required: requiredMessage, length: signupLength(128) },
    UserName: { display: "お名前", required: requiredMessage, length: signupLength(32) },
    Title: { display: "システム名", required: requiredMessage, length: signupLength(32) },
    Agree: { display: "利用規約に同意する", mustBeTrue: "利用規約に同意が必要です。" },
};

/** TenantEditViewModel・TenantUserEditViewModel の「入力してください」と「100 文字以内」。 */
const editText = (display: string): FieldRules => ({
    display,
    required: "{0}を入力してください。",
    length: { max: 100, message: "{0}は{1}文字以内で入力してください。" },
});

/** TenantEditViewModel（契約内容の変更）。 */
export const tenantEditRules: FormRules = {
    Company: editText("企業名/組織名"),
    UserName: editText("お名前"),
    Title: editText("システム名"),
};

/** TenantUserEditViewModel（利用者の編集）。 */
export const tenantUserEditRules: FormRules = {
    UserName: editText("お名前"),
};

/** 郵便番号・電話番号・メールアドレスの形式（BillingFormats の写し。<c>billing-formats.ts</c>）。 */
const postalCodeFormat = { valid: (value: string) => normalizePostalCode(value) !== undefined, message: postalCodeMessage };
const telFormat = { valid: (value: string) => normalizeTel(value) !== undefined, message: telMessage };
const emailFormat = { valid: isEmailLike, message: "{0}の形式が正しくありません。" };

/**
 * ご契約者の連絡先（部署名・郵便番号・ご住所・お電話番号・メールアドレス。申し込みと契約内容の変更）。
 * <b>有償プランでは部署名のほかを必須</b>にし、無償プランでは任意（入っていれば形式と長さは見る）。
 * 必須と形式は ContractorInput の Validate の写し、長さは属性の写し。
 */
export const contractorRules = (paid: boolean): FormRules => {
    const required = paid ? { required: paidRequired } : {};
    return {
        ContractorDepartment: { display: "ご契約者の部署名", length: signupLength(128) },
        ContractorPostalCode: { display: "ご契約者の郵便番号", ...required, length: signupLength(8), format: postalCodeFormat },
        ContractorAddress: { display: "ご契約者のご住所", ...required, length: signupLength(256) },
        ContractorTel: { display: "ご契約者のお電話番号", ...required, length: signupLength(32), format: telFormat },
        ContractorEmail: { display: "ご契約者のメールアドレス", ...required, length: signupLength(256), format: emailFormat },
    };
};

/**
 * 見積書の作成（<c>POST /api/estimate</c>）の宛名。<b>前後の空白はサーバが落とす</b>ので、空白だけなら空として断る。
 * 長さは請求書の宛名（契約の企業名/組織名）と同じ 128 文字まで。
 */
export const estimateRules: FormRules = {
    Addressee: {
        display: "宛名", required: requiredMessage, length: { max: 128, message: "{0}は {1} 文字以内で入力してください。" },
    },
};
